import { useState, useEffect, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import LoadingButton from '../../Components/LoadingButton';
import Modal from '../../Components/Modal';
import Table from '../../Components/Table';
import FormAutocomplete from '../../Components/forms/FormAutocomplete';
import FormSelect from '../../Components/forms/FormSelect';
import FormSwitch from '../../Components/forms/FormSwitch';
import FormTextbox from '../../Components/forms/FormTextbox';

import { ContentBox, ContentContainer, TableBox } from '../../theme';
import {
  getParticipations,
  createParticipation,
  editParticipation,
  getChains,
} from '../../api/services';
import { useModal } from '../../hooks';
import { YEARS } from '../../constants/select';
import { PARTICIPATION_COLUMNS } from '../../constants/tables/columns';
import { PARTICIPATION_INITIAL_VALUES } from '../../constants/forms/initialValues';
import {
  PARTICIPATION_EDIT_SCHEMA,
  PARTICIPATION_ADD_SCHEMA,
} from '../../constants/forms/schemas';
import useAuth from '../../context/useAuth';

const Participation = () => {
  const [editData, setEditData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [participations, setParticipations] = useState(null);
  const [query] = useState({ isActive: true });

  const { setUser } = useAuth();

  const [open, setOpen] = useModal({ ref: false });

  const fetchData = useCallback(async () => {
    try {
      const res = await getParticipations();
      setParticipations(res);
      setLoading(false);
    } catch (error) {
      if (error.status === 401) setUser(null);
      toast.error(error.message);
    }
  }, [setUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditRequest = data => {
    setEditData(data.row);
    setOpen(true);
  };

  const handleOpen = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  return (
    <>
      <ContentContainer maxWidth="xl">
        <ContentBox>
          <TableBox>
            <Box sx={{ alignSelf: 'flex-end' }}>
              <Button
                disabled={loading}
                endIcon={<AddIcon />}
                onClick={handleOpen}
                variant="contained"
              >
                Nuevo
              </Button>
            </Box>

            <Table
              columns={PARTICIPATION_COLUMNS(handleEditRequest)}
              loading={loading}
              rows={participations}
            />
          </TableBox>
        </ContentBox>
      </ContentContainer>

      {/* New/edit Modal */}
      <Modal
        disabled={loading}
        onClose={handleClose}
        open={open}
        title={`${editData ? 'Editar' : 'Nueva'} participación`}
        width="sm"
      >
        <Formik
          initialValues={editData || PARTICIPATION_INITIAL_VALUES}
          onSubmit={async values => {
            try {
              setLoading(true);

              const { chain, percentage } = values;

              const data = {
                ...values,
                chain: chain._id,
                percentage: Number(percentage).toFixed(2),
              };

              editData
                ? await editParticipation(data)
                : await createParticipation(data);

              toast.success(
                `Participación ${editData ? 'editada' : 'creada'} con éxito.`
              );

              await fetchData();
              handleClose();
            } catch (error) {
              setLoading(false);

              if (error.status === 409) {
                setError(error.message);
                return;
              }

              if (error.status === 401) setUser(null);
              toast.error(error.message);
            }
          }}
          validationSchema={
            editData ? PARTICIPATION_EDIT_SCHEMA : PARTICIPATION_ADD_SCHEMA
          }
        >
          {({ values }) => (
            <Form>
              <Grid container spacing={2} sx={{ p: 2, mb: 4 }}>
                {error && (
                  <Grid item xs={12}>
                    <Alert
                      onClose={() => setError(null)}
                      severity="error"
                      sx={{
                        mb: 2,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'error.dark',
                      }}
                    >
                      {error}
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormSelect
                    disabled={loading}
                    label="Año"
                    name="year"
                    options={YEARS}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormAutocomplete
                    disabled={loading}
                    label="Cadena"
                    name="chain"
                    property="chainName"
                    query={query}
                    source={getChains}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormTextbox
                    disabled={loading}
                    label="Participación"
                    name="percentage"
                    percentage
                  />
                </Grid>

                {editData && (
                  <Grid item xs={12}>
                    <FormSwitch
                      disabled={loading}
                      name="isActive"
                      offLabel="Inactivo"
                      onLabel="Activo"
                    />
                  </Grid>
                )}
              </Grid>

              {/* Buttons */}
              <Box
                sx={{
                  justifyContent: 'space-between',
                  display: 'flex',
                  width: '100%',
                  px: 2,
                }}
              >
                <Button color="error" disabled={loading} onClick={handleClose}>
                  Cancelar
                </Button>
                <LoadingButton
                  disabled={values.percentage < 0.01 || values.percentage > 100}
                  icon={editData ? <EditIcon /> : <AddIcon />}
                  loading={loading}
                  text={editData ? 'Editar' : 'Guardar'}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default Participation;
